import { X } from "lucide-react";
import React, { useRef, useEffect } from "react";

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    className?: string;
    children: React.ReactNode;
    showCloseButton?: boolean; // New prop to control close button visibility
    isFullscreen?: boolean; // Default to false for backwards compatibility
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    className,
    showCloseButton = true, // Default to true for backwards compatibility
    isFullscreen = false,
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const contentClasses = isFullscreen
        ? "w-full h-full"
        : "relative w-full rounded-3xl";

    return (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4 overflow-y-auto">
            {!isFullscreen && (
                <div
                    className="fixed inset-0 h-full w-full opacity-[0.5] backdrop-blur-[32px] z-[100]"
                    onClick={onClose}
                ></div>
            )}
            <div
                ref={modalRef}
                className={`${contentClasses} ${className} relative z-[101] max-h-[90vh] flex flex-col overflow-hidden`}
                onClick={(e) => e.stopPropagation()}
            >
                {showCloseButton && (
                    <button
                        onClick={onClose}
                        className="absolute 2xl:right-9 2xl:top-8 xl:right-9 xl:top-7 lg:right-7 lg:top-10 p-2 bg-gray-200 text-gray-600 cursor-pointer hover:bg-[#0147AD] hover:text-white rounded-full transition-colors z-10"
                    >
                        <X className="w-5 h-5" />
                    </button>
                )}
                <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
};
